import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { getInvoices, getInvoiceSummary } from '../services/invoiceService';
import { getClients } from '../services/clientService';
import Badge from '../components/common/Badge';
import EmptyState from '../components/common/EmptyState';
import TableSkeleton from '../components/common/skeletons/TableSkeleton';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_VARIANTS = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  partially_paid: 'warning',
  overdue: 'error',
  cancelled: 'default',
};

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

function StatCard({ label, value, accent }) {
  return (
    <div className="card">
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${accent || 'text-neutral-900 dark:text-neutral-100'}`}>{value}</p>
    </div>
  );
}

export default function Billing() {
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data: summary } = useQuery({
    queryKey: ['invoiceSummary'],
    queryFn: getInvoiceSummary,
  });

  const { data: invoicesData, isLoading, isError } = useQuery({
    queryKey: ['invoices', { clientId, status, page }],
    queryFn: () =>
      getInvoices({ clientId: clientId || undefined, status: status || undefined, page, limit: 25 }),
    keepPreviousData: true,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
  });

  const invoices = invoicesData?.invoices ?? [];
  const pagination = invoicesData?.pagination;
  const clients = clientsData?.clients ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Billing</h1>
        <Link to="/billing/new" className="btn btn-primary flex items-center gap-2">
          <PlusIcon className="h-4 w-4" />
          New invoice
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="Outstanding balance"
          value={fmt(summary?.totalOutstanding)}
          accent="text-neutral-900"
        />
        <StatCard
          label="Overdue invoices"
          value={summary?.overdueCount ?? '—'}
          accent={summary?.overdueCount > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-900 dark:text-neutral-100'}
        />
        <StatCard
          label="Collected this month"
          value={fmt(summary?.paidThisMonth)}
          accent="text-green-600 dark:text-green-400"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select
          value={clientId}
          onChange={(e) => { setClientId(e.target.value); setPage(1); }}
          className="input w-full sm:w-52"
        >
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.lastName}, {c.firstName}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="input w-full sm:w-44"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={6} />
      ) : isError ? (
        <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load invoices.</div>
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={CurrencyDollarIcon}
          title={clientId || status ? 'No invoices match your filters' : 'No invoices yet'}
          description="Create your first invoice to start billing clients."
          action={
            !clientId && !status && (
              <Link to="/billing/new" className="btn btn-primary">New invoice</Link>
            )
          }
        />
      ) : (
        <>
          <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Invoice #</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Due</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Total</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Balance</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {invoices.map((inv) => {
                  const balance = parseFloat(inv.total) - parseFloat(inv.amountPaid);
                  return (
                    <tr key={inv.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <Link
                          to={`/billing/${inv.id}`}
                          className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                        >
                          {inv.invoiceNumber}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                        {inv.client
                          ? `${inv.client.firstName} ${inv.client.lastName}`
                          : '—'}
                      </td>
                      <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                        {format(parseISO(inv.invoiceDate), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-neutral-600">
                        {format(parseISO(inv.dueDate), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-right text-neutral-900 dark:text-neutral-100 font-medium">
                        {fmt(inv.total)}
                      </td>
                      <td className="px-6 py-4 text-right font-medium">
                        <span className={balance > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-400 dark:text-neutral-500'}>
                          {fmt(balance)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          label={inv.status.replace('_', ' ')}
                          variant={STATUS_VARIANTS[inv.status] || 'default'}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-neutral-600 dark:text-neutral-400">
              <span>
                {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => p - 1)} disabled={page === 1} className="btn btn-secondary disabled:opacity-40">Previous</button>
                <button onClick={() => setPage((p) => p + 1)} disabled={page === pagination.pages} className="btn btn-secondary disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
